import { RootState } from '../../store'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  computeAvailableForBetCombinedExposer,
  computeRemainingBalance,
  computeWithdrawableMainCombinedExposer,
} from '../../../utils/availableBalance'

export type HideBalExp = { balance: boolean; exposer: boolean }

export type BalanceRow = {
  balance: number
  exposer: number
  bonusBalance?: number
  pendingBonusUsed?: number
  availableBalance?: number
  /** Unsettled PnL since last transfer (wallet Winnings). */
  profitLoss?: number
  /** Live credit limit from User.creditRefrences (admin CRD). */
  creditRefrences?: number
}

export type BalanceState = {
  balance: BalanceRow
  hide: HideBalExp
}

const initialState: BalanceState = {
  balance: {
    balance: 0,
    exposer: 0,
    bonusBalance: 0,
    pendingBonusUsed: 0,
    availableBalance: 0,
    profitLoss: 0,
    creditRefrences: undefined,
  },
  hide: { balance: false, exposer: false },
}

const balanceSlice = createSlice({
  name: 'balance',
  initialState,
  reducers: {
    setBalance: (
      state,
      action: PayloadAction<{
        balance: number
        exposer: number
        bonusBalance?: number
        pendingBonusUsed?: number
        availableBalance?: number
        profitLoss?: number
        creditRefrences?: number
      }>,
    ) => {
      const next: BalanceRow = {
        balance: action.payload.balance ?? state.balance.balance,
        exposer: action.payload.exposer ?? state.balance.exposer,
        bonusBalance: action.payload.bonusBalance ?? state.balance.bonusBalance ?? 0,
        pendingBonusUsed:
          action.payload.pendingBonusUsed ?? state.balance.pendingBonusUsed ?? 0,
        profitLoss:
          action.payload.profitLoss !== undefined
            ? action.payload.profitLoss
            : state.balance.profitLoss ?? 0,
        creditRefrences:
          action.payload.creditRefrences !== undefined
            ? action.payload.creditRefrences
            : state.balance.creditRefrences,
        availableBalance:
          action.payload.availableBalance ??
          computeRemainingBalance({
            balance: action.payload.balance ?? state.balance.balance,
            exposer: action.payload.exposer ?? state.balance.exposer,
            pendingBonusUsed:
              action.payload.pendingBonusUsed ?? state.balance.pendingBonusUsed ?? 0,
          }),
      }
      state.balance = next
    },
    setExposer: (state, action: PayloadAction<number>) => {
      state.balance = { ...state.balance, exposer: action.payload }
    },
    setSingleBal: (state, action: PayloadAction<number>) => {
      state.balance = { ...state.balance, balance: action.payload }
    },
    hideBalExp: (state, action: PayloadAction<HideBalExp>) => {
      state.hide = action.payload
    },
  },
})

export const { setBalance, setExposer, hideBalExp, setSingleBal } = balanceSlice.actions
export const selectBalance = (state: RootState) => state.balanceReducer.balance
export const selectHideBalExp = (state: RootState) => state.balanceReducer.hide

/** Main available after exposure (API merges sports+casino into `exposer`). Bonus wallet separate — not included. */
export function computeAvailableForBet(b: {
  balance: number
  exposer: number
  bonusBalance?: number
}): number {
  return computeAvailableForBetCombinedExposer(b)
}

/** Main available after exposure (same as header remaining balance). */
export function computeAvailableMainAfterExposure(b: BalanceRow): number {
  return computeRemainingBalance(b)
}

export { computeRemainingBalance }

export function mapBalanceApiToStore(raw: {
  balance?: number
  exposer?: number
  bonusBalance?: number
  pendingBonusUsed?: number
  availableBalance?: number
  profitLoss?: number
  creditRefrences?: number | string
} | null | undefined): BalanceRow | null {
  if (!raw || typeof raw !== 'object' || typeof raw.balance !== 'number') return null
  const profitLoss =
    typeof raw.profitLoss === 'number' && Number.isFinite(raw.profitLoss) ? raw.profitLoss : 0
  const crRaw = raw.creditRefrences
  const creditRefrences =
    crRaw !== undefined && crRaw !== null && crRaw !== '' && Number.isFinite(Number(crRaw))
      ? Number(crRaw)
      : undefined
  const row: BalanceRow = {
    balance: raw.balance,
    exposer: typeof raw.exposer === 'number' ? raw.exposer : 0,
    bonusBalance: typeof raw.bonusBalance === 'number' ? raw.bonusBalance : 0,
    pendingBonusUsed: typeof raw.pendingBonusUsed === 'number' ? raw.pendingBonusUsed : 0,
    profitLoss,
    creditRefrences,
    availableBalance:
      typeof raw.availableBalance === 'number'
        ? raw.availableBalance
        : computeRemainingBalance({
            balance: raw.balance,
            exposer: typeof raw.exposer === 'number' ? raw.exposer : 0,
            pendingBonusUsed: typeof raw.pendingBonusUsed === 'number' ? raw.pendingBonusUsed : 0,
          }),
  }
  return row
}

export default balanceSlice
